"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import { Bell, Check, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

type Notification = {
  id: string;
  title: string;
  description: string;
  time: string;
  read: boolean;
  type: "alert" | "achievement" | "update";
  link?: string;
};

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    // mouse + touch so the panel collapses on tap-outside for mobile users too
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener(
      "touchstart",
      handleClickOutside as EventListener,
    );
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener(
        "touchstart",
        handleClickOutside as EventListener,
      );
    };
  }, []);

  // Close on Escape (keyboard + accessibility)
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setIsOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .getNotificationHistory(1)
      .then((res: any[]) => {
        if (!active) return;
        const mapped: Notification[] = (res || []).map((n) => ({
          id: n.id,
          title: n.message,
          description: n.description || n.body || "",
          link: n.link,
          time: n.timestamp ? new Date(n.timestamp).toLocaleString() : "",
          read: n.isRead,
          type: n.type || "update",
        }));
        setNotifications(mapped.slice(0, 20));
      })
      .catch(() => {
        if (!active) return;
        setNotifications([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markRead = async (id: string) => {
    // optimistic local update; mirror to server best-effort
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    try {
      await api.markNotificationRead(id);
    } catch {
      /* best-effort */
    }
  };

  const markAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await api.markAllNotificationsRead();
    } catch {
      // best-effort
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-xl hover:bg-[var(--glass-bg)] transition-colors"
      >
        <Bell className="w-5 h-5 text-[var(--icon-color)] hover:text-y transition-colors" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-1 right-1 h-4 w-4 bg-y rounded-full flex items-center justify-center text-xs font-black text-b"
          >
            {unreadCount}
          </motion.span>
        )}
      </motion.button>

      {/* Mobile tap-to-dismiss backdrop (desktop keeps anchored dropdown) */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[140] bg-black/50 sm:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: "spring", bounce: 0.2, duration: 0.3 }}
            /*
              Mobile: a_fixed, viewport-anchored sheet. The old `absolute right-0`
              + `w-[min(24rem,100vw-1rem)]` anchored the panel's right_edge_to_the
              bell, so on narrow screens the panel ran off-screen to the LEFT
              whenever the bell wasn't flush right — the "notification opens badly
              on mobile" report. Desktop (sm+) keeps the anchored dropdown.
            */
            className="fixed left-2 right-2 top-16 w-auto max-w-none sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-w-[calc(100vw-1rem)] bg-card border border-y/20 rounded-2xl shadow-2xl overflow-hidden z-[150] sm:origin-top-right"
          >
            <div className="p-4 border-b border-border/30 flex items-center justify-between">
              <h3 className="font-bebas text-lg text-y tracking-wider">
                Notifications
              </h3>
              <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider">
                {unreadCount} unread
              </span>
            </div>

            <div className="max-h-[65vh] sm:max-h-80 overflow-y-auto overscroll-contain">
              {loading ? (
                <div className="p-4 text-xs text-muted-foreground text-center">
                  Loading...
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-4 text-xs text-muted-foreground text-center">
                  No notifications yet.
                </div>
              ) : (
                notifications.map((notif) => (
                  <motion.div
                    key={notif.id}
                    whileHover={{ x: 4 }}
                    className={`p-4 border-b border-[var(--glass-border)] hover:bg-[var(--glass-bg)] transition-colors cursor-pointer ${notif.read ? "opacity-60" : "bg-y/5"}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-1 w-2 h-2 rounded-full ${notif.type === "achievement" ? "bg-y" : notif.type === "alert" ? "bg-red-400" : "bg-blue-400"}`}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-bold text-foreground">
                          {notif.title}
                        </p>
                        <p className="text-xs text-[var(--text-muted)] mt-0.5">
                          {notif.description || notif.title}
                        </p>
                        {notif.link ? (
                          <a
                            href={notif.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-y underline mt-1 block"
                          >
                            Open
                          </a>
                        ) : null}
                        <span className="text-xs text-[var(--text-faint)] font-medium mt-1 block">
                          {notif.time}
                        </span>
                      </div>
                      {!notif.read && (
                        <button
                          onClick={() => markRead(notif.id)}
                          aria-label="Mark as read"
                          className="p-1 hover:text-y transition-colors"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {notifications.length > 0 && (
              <div className="p-3 border-t border-[var(--glass-border)] flex justify-center">
                <button
                  onClick={markAllRead}
                  className="text-xs font-black uppercase tracking-widest text-y hover:text-white transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear all
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
